import {
  Box,
  Button,
  Columns,
  Container,
  Heading,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import {
  backgroundColor,
  height,
  match,
  minHeight,
  paddingBottom,
} from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { HeaderProps } from 'components';
import { productIcons, SuperPrismicPortfolio } from 'helpers/products';
import { useSuperSignupOneLink } from 'helpers/use-attribution-link';
import React from 'react';
import styled from 'styled-components';

import DottedCircle from './img-dots.svg';
import BalancedDesktopImage from './img-hero-balanced.svg';
import GlobalIndexDesktopImage from './img-hero-global-index.svg';
import GrowthXDesktopImage from './img-hero-growthx.svg';
import ModerateDesktopImage from './img-hero-moderate.svg';

interface PortfolioHeroPanelProps {
  headerComponent: React.FC<HeaderProps>;
  product?: string;
  title?: string;
  tagline?: string | React.ReactNode;
  portfolio: SuperPrismicPortfolio;
}

const DotsBackground = styled('div')`
  ${backgroundColor('neutral.100')}
  ${minHeight({ xs: 'none', md: '660px', lg: '800px' })}
  ${paddingBottom({
    xs: 'xxl',
    md: 'none',
  })}
  background-image: url(${DottedCircle});
  background-size: auto;
  background-repeat: no-repeat;
  background-position: center;
  ${match('md')`
    background-position: top 65% left -10%;
  `};

  ${match('lg')`
    background-position: top 75% left 50%;
  `};
`;

const HeroImage = styled('div')<{ backgroundImageURL: string }>`
  background-image: url(${({ backgroundImageURL }) => backgroundImageURL});
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  ${height({ xs: 'none', md: '476px', lg: '600px' })}
  height: auto;
  width: auto;
`;

export const PortfolioHeroPanel: React.FC<PortfolioHeroPanelProps> = ({
  product,
  title,
  tagline,
  portfolio,
  headerComponent: HeaderComponent,
}) => {
  const PortfolioIcon = productIcons[portfolio];
  const desktopImage = {
    growthx: GrowthXDesktopImage,
    global_index: GlobalIndexDesktopImage,
    balanced: BalancedDesktopImage,
    moderate: ModerateDesktopImage,
  }[portfolio];

  const signupHref = useSuperSignupOneLink();

  return (
    <TrackingCategory category="Hero Panel">
      <DotsBackground>
        <Stack spaceY={{ xs: 'lg', md: 'md' }}>
          <HeaderComponent buttonVariant="primary" />
          <Container>
            <Columns alignY="center">
              <Columns.Column width={{ xs: 1, md: 4 / 8, lg: 6 / 12 }}>
                <HeroImage backgroundImageURL={desktopImage} />
              </Columns.Column>
              <Columns.Column width={{ xs: 1, md: 4 / 8, lg: 6 / 12 }}>
                <Box marginLeft={{ xs: 'none', md: 'lg', lg: 'none' }}>
                  <Stack
                    spaceY={{ xs: 'md', md: 'md', lg: 'lg' }}
                    alignX={{ xs: 'center', md: 'left' }}
                  >
                    <Visible
                      isHidden={{ xs: false, md: true }}
                      displayValue="block"
                    >
                      <PortfolioIcon color="neutral.000" size="xxxl" />
                    </Visible>
                    <Stack spaceY={{ xs: 'sm', md: 'md' }}>
                      <Text
                        variant={2}
                        color="neutral.070"
                        align={{ xs: 'center', md: 'left' }}
                      >
                        {product}
                      </Text>
                      <Heading
                        variant={1}
                        component="h1"
                        color="neutral.000"
                        align={{ xs: 'center', md: 'left' }}
                      >
                        {title}
                      </Heading>
                      <Columns>
                        <Columns.Column width={{ xs: 1, lg: 5 / 6 }}>
                          <Text
                            variant={1}
                            color="neutral.070"
                            align={{ xs: 'center', md: 'left' }}
                          >
                            {tagline}
                          </Text>
                        </Columns.Column>
                      </Columns>
                    </Stack>

                    <Button size="lg" variant="primary" href={signupHref}>
                      Sign up
                    </Button>
                  </Stack>
                </Box>
              </Columns.Column>
            </Columns>
          </Container>
        </Stack>
      </DotsBackground>
    </TrackingCategory>
  );
};
